import Primus from "./primus-client";
import { RestClient } from "./restClient";
import { apm } from "@elastic/apm-rum";
import objectHash from "object-hash";
import { EventEmitter } from "events";

let instance = null;
export class RealtimeClient extends EventEmitter {
	constructor(options) {
		super();
		if (!instance) {
			// -- initialization
			this._options = options;
			this._connected = false;
			this._subs = {};
			this._subHashes = {};
			this._primus = null;
			this._resubscribe = false;
			this._isOffline = false;
			this._isReconnecting = false;
			this._sessionEndHandler = null;
			this._isLoggingOut = false;
			this._restClient = new RestClient();
			this._realtimeConfig = { pingTimeout: 5000, activityInterval: 2000 };
			this._connecting = false;
			this._resolveReady = null;
			this._armReady();
			this._lastActivity = Date.now();

			this._restClient.exec_get("/_realtime/config", (err, result) => {
				if (err) {
					console.log(
						"Error getting realtime config. Using defaults",
						this._realtimeConfig,
						err.message,
						err.stack
					);
				} else {
					this._realtimeConfig = { ...this._realtimeConfig, ...result };
				}
				this._init();
			});
			instance = this;
		}
		return instance;
	}
	_armReady() {
		this.ready = new Promise(res => {
			this._resolveReady = res;
		});
	}
	async _ensureConnected() {
		if (this._primus && this._connected) return;
		if (!this._primus && !this._connecting) this._open(); // kick off connect
		await this.ready; // resolved by the 'open' event
	}

	set sessionEndHandler(handler) {
		if (typeof handler === "function") {
			this._sessionEndHandler = handler;
		} else {
			throw new Error("handler must be a function");
		}
	}

	subscribe(app, route, args, callback, oldChannel, autoResub = true) {
		const s = {
			method: "subscribe",
			app: app,
			route: route,
			args: args,
			autoResub
		};
		const hash = objectHash(s);
		if (!this._subHashes[hash]) {
			const sub = { s, hash, callback };
			this._subHashes[hash] = { subs: [sub] };
		} else {
			const sub = { s, hash, callback, channel: "shared" };
			this._subHashes[hash].subs.push(sub);
			return new Promise((resolve, reject) => {
				console.log("Sharing existing channel", sub);
				resolve(sub);
			});
		}
		const currentTxn = apm.getCurrentTransaction();
		let span = null;
		if (currentTxn) {
			span = currentTxn.startSpan(s.route, "realtime", {
				blocking: false
			});
			setTimeout(() => {
				if (span && !span.ended) {
					span.addLabels({
						message: "subscription did not return a value after 2 seconds"
					});
					span.end();
				}
			}, 2000);
		}
		return new Promise(async resolve => {
			await this._ensureConnected(); // ---- NEW: wait for primus ----
			this._primus.writeAndWait(
				s,
				function (sub) {
					this._subs[sub.channel] = {};
					this._subs[sub.channel]["hash"] = hash;
					this._subs[sub.channel]["request"] = s;
					this._subs[sub.channel]["callback"] = callback;
					this._subs[sub.channel]["channel"] = sub.channel;
					if (span) {
						this._subs[sub.channel]["span"] = span;
					}
					this._subs[sub.channel]["unsubscribe"] = () => {
						return this._unsubscribe(sub.channel);
					};
					if (oldChannel) this._subs[sub.channel]["oldChannel"] = oldChannel;
					console.debug("subscription response:", sub);
					resolve(this._subs[sub.channel]);
				}.bind(this)
			);
		});
	}

	_unsubscribe(channel) {
		if (channel === "shared") {
			console.log("Shared channel Does not need to be unsubscribed", channel);
			return new Promise(resolve => {
				resolve(true);
			});
		}

		const unsubscribe = {
			method: "unsubscribe",
			channel: null
		};

		const sub = this._subs[channel] || Object.values(this._subs).find(s => s.oldChannel === channel);
		if (sub) {
			if (this._subHashes[sub.hash]) delete this._subHashes[sub.hash];
			unsubscribe.channel = sub.channel || channel;
		}

		return new Promise(resolve => {
			// -- channel not found
			if (!unsubscribe.channel) {
				resolve(false);
			} else {
				this._primus.writeAndWait(
					unsubscribe,
					function (res) {
						if (res.unsubscribed) {
							delete this._subs[res.channel];
						}
						resolve(res.unsubscribed);
					}.bind(this)
				);
			}
		});
	}

	_init() {
		this._restClient.exec_get("/ecosystem/api/loggedInUser", (err, result) => {
			if (err) {
				console.debug("No logged in user", err);
			} else {
				this._open();
			}
		});
	}

	_open() {
		if (this._connecting) return;
		this._connecting = true;
		const primusOptions = {
			reconnect: {
				max: 0, // Number: The max delay before we try to reconnect.
				min: 0, // Number: The minimum delay before we try reconnect.
				retries: 0 // Number: How many times we should try to reconnect.
			},
			transport: ["websockets"],
			strategy: false,
			manual: true,
			pingTimeout: false
		};

		const isDev = window.location.origin.includes(":3000");
		const primusEndpoint = isDev ? "https://localhost:3000" : undefined;
		this._primus = Primus.connect(primusEndpoint, primusOptions);
		this._primus.open();

		// Monitor for inactivity
		this._activityCheckInterval = setInterval(() => {
			if (this._isOffline) {
				console.log("Skipping activity check: offline");
				return;
			}
			const timeSinceActivity = Date.now() - this._lastActivity;
			if (timeSinceActivity > this._realtimeConfig.pingTimeout) {
				console.log(`No activity for ${timeSinceActivity}ms, assuming server dead`);
				this.forceReconnect();
			} else {
				console.debug(`Last activity ${timeSinceActivity}ms ago, connection alive`);
			}
		}, this._realtimeConfig.activityInterval);

		this._primus.on(
			"open",
			function open() {
				this._connected = true;
				this._connecting = false;
				this.emit("connected");
				this._lastActivity = Date.now();
				if (this._resolveReady) {
					this._resolveReady();
					this._resolveReady = null;
				}
				if (this._resubscribe) this.resubscribeAll();
			}.bind(this)
		);

		this._primus.on(
			"data",
			function message(res) {
				this._lastActivity = Date.now();
				if (window.__LOG_RT_TS__) {
					if (res.appTs) {
						if (!window.__LOG_RT_TS__?.filter || res.msg.includes(window.__LOG_RT_TS__?.filter)) {
							const gatewayAppDiff = new Date(res.gatewayTs).getTime() - new Date(res.appTs).getTime();
							const recvd = Date.now();
							const clientGatewayDiff = recvd - new Date(res.gatewayTs).getTime();
							const logObj = {
								...res,
								clientRecvdTs: new Date(recvd).toISOString(),
								subscription: this._subs[res.channel]
							};
							console.debug(
								"RT_TS_LOG:",
								logObj,
								"\nApp->Gateway:",
								gatewayAppDiff,
								"ms",
								"\nGateway->Client:",
								clientGatewayDiff,
								"ms"
							);
						}
					}
				}
				this.emit("dataReceived", JSON.stringify(res).length); // Emit dataReceived event with byte size
				if (res.heartbeat) {
					console.debug("heartbeat received at", new Date().toISOString());
				} else if (res.isSystemEvent) {
					switch (res.eventType) {
						case "session-end":
							clearInterval(this._activityCheckInterval);
							switch (res.reason) {
								case "switched-user":
									// Add slight delay to reduce Unauthorized issues for switched-to user
									setTimeout(() => {
										window.location.reload(true);
									}, 100);
									break;
								default:
									console.debug(`Reason for session end: ${res.reason}`, res);
									this._isLoggingOut = true;
									break;
							}
							this._primus.destroy();
							if (this._sessionEndHandler) {
								this._sessionEndHandler();
							}
							break;
						default:
							console.log(`Unhandled system event: ${res.eventType}`, res);
							break;
					}
				} else if (!res.channel) {
					console.warn("Received message without channel:", res);
				} else if (!this._subs[res.channel]) {
					console.warn("Received message for unknown channel:", res.channel, res);
				} else {
					const hash = this._subs[res.channel].hash ? this._subHashes[this._subs[res.channel].hash] : null;
					if (hash) {
						if (hash.subs) {
							for (const sub of hash.subs) {
								const handler = sub.callback;
								if (handler) {
									const { autoResub } = this._subs?.[res.channel]?.request;
									try {
										if (!this._subs[res.channel]._fr) {
											if (this._subs[res.channel].span) {
												this._subs[res.channel].span.end();
											}
											this._subs[res.channel]._fr = true;
										}
										if (res.err) {
											console.error(
												"channel error:",
												res.err.message,
												"\n",
												this._subs[res.channel]
											);
											if (autoResub && !(res?.err?.code === 401 || res?.err?.code === 403)) {
												this.resubscribe(this._subs[res.channel]);
											} else {
												// let caller handle the error
												if (!autoResub) handler({ err: res.err });
											}
											break;
										} else {
											handler(res.msg);
										}
									} catch (err) {
										console.error(`Error in subscription handler for channel ${res.channel}:`, err);
										if (autoResub) this.resubscribe(sub);
										else handler({ err: res.err });
									}
								}
							}
						} else {
							console.log("Hash has no subs:", hash);
						}
					} else {
						console.log("Hash not found for channel:", {
							channel: res.channel,
							subscription: this._subs[res.channel],
							availableHashes: Object.keys(this._subHashes)
						});
					}
				}
			}.bind(this)
		);

		this.resubscribe = async oldSub => {
			const oldChannel = this._subs[oldSub.channel]?.["oldChannel"] || oldSub?.channel;
			if (oldSub) oldSub.unsubscribe();
			this.subscribe(
				oldSub.request.app,
				oldSub.request.route,
				oldSub.request.args,
				oldSub.callback,
				oldChannel,
				oldSub.request.autoResub || true
			);
		};

		this.resubscribeAll = () => {
			console.log("Resubscribing to all channels...");
			Object.keys(this._subs).forEach(
				function (key) {
					const s = this._subs[key];
					this.resubscribe(s);
				}.bind(this)
			);
		};

		// Handle browser offline/online events
		window.addEventListener("offline", () => {
			this._isOffline = true;
			this.emit("offline");
			console.log("Browser went offline");
			this._primus.end(); // Stop any polling attempts
		});

		window.addEventListener("online", () => {
			this._isOffline = false;
			this.emit("online");
			console.log("Browser back online, reconnecting...");
			this.forceReconnect();
		});

		// Force reconnect function
		this.forceReconnect = () => {
			if (this._isOffline || this._isReconnecting) {
				console.log("Skipping reconnect: offline or already reconnecting");
				return;
			}
			this._isReconnecting = true;
			console.log("No activity detected, forcing reconnect...");
			this._resubscribe = true;
			this._primus.end();
			setTimeout(() => {
				this._primus.open();
				this._isReconnecting = false;
			}, 100);
		};

		// Handle connection end
		this._primus.on("end", () => {
			console.log("Connection ended");
			this._connected = false;
			this._connecting = false;
			this.emit("disconnected");
			if (!this._isOffline && !this._isLoggingOut) {
				this.forceReconnect();
			}
		});

		// Error handling
		this._primus.on("error", err => {
			console.log("Error:", err.message);
			if (err.message.includes("ECONNREFUSED") && !this._isOffline) {
				this.forceReconnect(); // Hard failure, reconnect now
			}
		});
	}
}
